/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useTimezoneManager } from '@sportninja/common/hooks/useTimezoneManager'
import { t } from '@sportninja/common/i18n'
import React, { useEffect, useState } from 'react'
import Helmet from 'src/components/Helmet'
import Icon from 'src/components/Icon'
import Picture from 'src/components/Picture'
import { imageCss } from '../GamePageHeaderMatchupCss'
import { AutoPlayButtonMobile, MatchDateTime } from './components'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import colors from '@sportninja/common/constants/appColors'
import { getYoutubeVideoId, isValidVideoUrl } from 'src/utils/video'
import { isTimeToBeDefined } from '@sportninja/common/ts-utils/date-utils'

const NewMobileGamePageHeader = ({
  videoUrl,
  game,
  organization,
  actions,
  fullGameType,
  isMobile,
  windowWidth,
}) => {
  console.log('📱 NewMobileGamePageHeader: Rendering with video URL', {
    videoUrl,
  })

  const isHostedOnS3 = videoUrl?.includes('s3.amazonaws.com')
  const { homeTeam = {}, visitingTeam = {} } = game
  const homePlaceholder = homeTeam?.isPlaceholder
  const visitingPlaceholder = visitingTeam?.isPlaceholder
  const picProps = { iconName: 'user-friends', square: true, fit: true }
  const { locationDate, getDateString } = useTimezoneManager({
    date: game.startsAt,
    timezone: game?.location?.timezone,
    isTimeToBeDefined: isTimeToBeDefined(game.startsAt),
  })

  // Video state management
  const [autoPlay, setAutoPlay] = useState(false)
  const [videoError, setVideoError] = useState(false)
  const [isPlayerReady, setIsPlayerReady] = useState(false)
  const isValidVideo = isValidVideoUrl(videoUrl)

  const videoRef = React.useRef(null)
  const playerRef = React.useRef(null)
  const isHomeWinner = game.homeTeamScore > game.visitingTeamScore
  const isVisitingWinner = game.visitingTeamScore > game.homeTeamScore

  const onPlayerStateChange = (event) => {
    if (event.data === window.YT.PlayerState.PLAYING) {
      setAutoPlay(true)
    }
    if (event.data === window.YT.PlayerState.PAUSED) {
      setAutoPlay(false)
    }
  }

  const onPlayerReady = (event) => {
    console.log('✅ Mobile YouTube player ready')
    setIsPlayerReady(true)
    setVideoError(false)
  }

  const onPlayerError = (event) => {
    console.error('❌ Mobile YouTube player error:', event.data)
    setVideoError(true)
    setIsPlayerReady(false)
  }

  const handleRetryVideo = () => {
    console.log('🔄 handleRetryVideo: Attempting to retry mobile video')
    setVideoError(false)
    setIsPlayerReady(false)
    setAutoPlay(false)
  }

  useEffect(() => {
    if (
      window.YT &&
      !isHostedOnS3 &&
      videoUrl &&
      isValidVideo &&
      !isPlayerReady &&
      !videoError
    ) {
      try {
        console.log('📱 Initializing mobile YouTube player')
        playerRef.current = new window.YT.Player('player', {
          height: '390',
          width: windowWidth,
          playerVars: {
            autoplay: 0,
            controls: 1,
            playlist: getYoutubeVideoId(videoUrl),
            rel: 0,
          },
          videoId: getYoutubeVideoId(videoUrl),
          events: {
            onStateChange: onPlayerStateChange,
            onReady: onPlayerReady,
            onError: onPlayerError,
          },
        })
      } catch (error) {
        console.error('❌ Failed to initialize mobile YouTube player:', error)
        setVideoError(true)
      }
    }
  }, [
    isHostedOnS3,
    videoUrl,
    windowWidth,
    isValidVideo,
    isPlayerReady,
    videoError,
  ])

  let title = game.gameNumber
    ? `${t('util:capitalize', { text: 'Game' })} #${game.gameNumber}`
    : game.gameNumber === null || game.gameNumber?.length === 0
    ? t('util:capitalize', { text: 'Game' })
    : `${'Loading'}...`

  useEffect(() => {
    const playVideo = async () => {
      try {
        if (videoRef.current && autoPlay && !videoError) {
          console.log('📱 Attempting to play mobile S3 video')
          await videoRef.current.play()
        }
      } catch (error) {
        console.error('❌ Error playing mobile S3 video:', error)
        setVideoError(true)
        setAutoPlay(false)
      }
    }

    if (isHostedOnS3) {
      playVideo()
    }
  }, [videoRef, autoPlay, videoError, isHostedOnS3])

  return (
    <div
      css={css`
        width: ${windowWidth}px;
        height: 360px;
        background: 'linear-gradient(180deg, rgba(6, 10, 16, 0.3) 0%, #181d26 100%)';
        position: relative;
        .ytp-more-videos-view {
          display: none !important;
        }
      `}
      onClick={() => {
        if (autoPlay) {
          setAutoPlay(false)
          videoRef.current?.pause()
        }
      }}
    >
      <Helmet title={title} />
      {!videoError && isValidVideo ? (
        isHostedOnS3 ? (
          <video
            ref={videoRef}
            src={videoUrl || ''}
            width={windowWidth}
            height='390'
            // eslint-disable-next-line react-native/no-inline-styles
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              border: 'none',
              background:
                'linear-gradient(180deg, rgba(6, 10, 16, 0.3) 0%, #181d26 100%)',
              objectFit: 'cover',
            }}
            autoPlay={autoPlay}
            loop
            muted
            controls={autoPlay ? true : false}
            onPause={() => {
              setAutoPlay(false)
            }}
            onError={() => {
              console.error('❌ Mobile S3 video element error')
              setVideoError(true)
            }}
          />
        ) : (
          <div
            id='player'
            // eslint-disable-next-line react-native/no-inline-styles
            style={{
              width: windowWidth,
              height: 390,
              position: 'absolute',
              top: 0,
              left: 0,
              border: 'none',
              background:
                'linear-gradient(180deg, rgba(6, 10, 16, 0.3) 0%, #181d26 100%)',
            }}
          />
        )
      ) : (
        <div
          css={css`
            width: ${windowWidth}px;
            height: 390px;
            position: absolute;
            top: 0;
            left: 0;
            background: linear-gradient(
              180deg,
              rgba(6, 10, 16, 0.3) 0%,
              #181d26 100%
            );
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            gap: 12px;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 12px 16px;
              background: rgba(0, 0, 0, 0.7);
              border-radius: 8px;
              backdrop-filter: blur(10px);
            `}
          >
            <Icon
              name='exclamation-triangle'
              fontSize={18}
              color={colors.WARNING}
            />
            <span
              css={css`
                color: ${colors.WHITE};
                font-size: 14px;
                font-weight: 500;
              `}
            >
              {!isValidVideo ? 'Invalid video URL' : 'Video unavailable'}
            </span>
          </div>
          {videoError && (
            <button
              css={css`
                padding: 6px 12px;
                background: ${colors.PRIMARY};
                color: ${colors.WHITE};
                border: none;
                border-radius: 4px;
                font-size: 12px;
                font-weight: 500;
                cursor: pointer;
                transition: background 0.2s;
                &:hover {
                  background: ${colors.PRIMARY_DARK || colors.PRIMARY};
                }
              `}
              onClick={handleRetryVideo}
            >
              Retry
            </button>
          )}
        </div>
      )}
      {!videoError && isValidVideo && (
        <div
          css={css`
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            cursor: pointer;
            ${autoPlay && 'display: none;'}
          `}
          onClick={() => {
            setAutoPlay(!autoPlay)
            if (playerRef.current) {
              playerRef.current.playVideo()
            }
          }}
        >
          <AutoPlayButtonMobile />
        </div>
      )}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
          position: absolute;
          bottom: 4px;
          left: 4px;
          backdrop-filter: blur(50px);
          padding: 8px;
          border-radius: 8px;
          width: calc(100% - 16px);
          ${autoPlay && !videoError && 'display: none;'}
        `}
      >
        {/* Team Info */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
            align-self: stretch;
          `}
        >
          {/* Home Team */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              align-self: stretch;
            `}
          >
            <Picture
              imageId={homeTeam?.imageId}
              css={imageCss}
              size='skxxsmall'
              {...picProps}
            />
            <p
              css={css`
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 1;
                flex: 1 0 0;
                overflow: hidden;
                color: var(--Neutrals-White, #fff);
                font-feature-settings: 'cv04' on, 'cv03' on, 'cv01' on;
                text-overflow: ellipsis;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                font-size: 28px;
                font-weight: 600;
                line-height: 120%; /* 48px */
              `}
            >
              {homePlaceholder ? 'Home Team' : homeTeam?.fullName}
            </p>
            <div
              css={css`
                display: flex;
                padding: 4px 16px;
                align-items: center;
                gap: 8px;
                border-radius: 8px;
                background: ${isHomeWinner ? colors.PRIMARY : '#27303E'};
              `}
            >
              <p
                css={css`
                  color: ${isHomeWinner ? '#27303E' : '#ffffff'};
                  text-align: center;
                  font-feature-settings: 'cv04' on, 'cv03' on, 'cv01' on;

                  /* Heading 1/Semibold */
                  font-family: ${isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                  font-size: 28px;
                  font-style: normal;
                  font-weight: 600;
                  line-height: 120%; /* 48px */
                `}
              >
                {game.homeTeamScore}
              </p>
            </div>
          </div>
          {/* Visiting Team */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              align-self: stretch;
            `}
          >
            <Picture
              imageId={visitingTeam?.imageId}
              css={imageCss}
              size='skxxsmall'
              {...picProps}
            />
            <p
              css={css`
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 1;
                flex: 1 0 0;
                overflow: hidden;
                color: var(--Neutrals-White, #fff);
                font-feature-settings: 'cv04' on, 'cv03' on, 'cv01' on;
                text-overflow: ellipsis;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                font-size: 28px;
                font-weight: 600;
                line-height: 120%; /* 48px */
              `}
            >
              {visitingPlaceholder ? 'Home Team' : visitingTeam?.fullName}
            </p>
            <div
              css={css`
                display: flex;
                padding: 4px 16px;
                align-items: center;
                gap: 8px;
                border-radius: 8px;
                background: ${isVisitingWinner ? colors.PRIMARY : '#27303E'};
              `}
            >
              <p
                css={css`
                  color: ${isVisitingWinner ? '#27303E' : '#ffffff'};
                  text-align: center;
                  font-feature-settings: 'cv04' on, 'cv03' on, 'cv01' on;

                  /* Heading 1/Semibold */
                  font-family: ${isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                  font-size: 28px;
                  font-style: normal;
                  font-weight: 600;
                  line-height: 120%; /* 48px */
                `}
              >
                {game.visitingTeamScore}
              </p>
            </div>
          </div>
          <div>
            {/* Date and Time */}
            {game.startsAt && (
              <MatchDateTime isDesktop={true}>
                {game.startsAt && fullGameType && (
                  <span>{fullGameType.name}</span>
                )}
              </MatchDateTime>
            )}
            {game.startsAt && (
              <MatchDateTime isDesktop={true}>
                {getDateString(locationDate, "MMM dd, y 'at' h:mm a z")}
              </MatchDateTime>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewMobileGamePageHeader
